import React, { useState, useEffect } from 'react';
import { getFullApiUrl } from '../config';
import config from '../config';
import { addAuthHeader } from '../utils/auth';

const SmartUploadSection = ({ onProcessingComplete, onProcessingChange }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [processingSteps, setProcessingSteps] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFiles(files); // Accept multiple files
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFiles(files); // Accept multiple files
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadStatus(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Prevent page navigation when processing
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isProcessing) {
        // Standard way to show confirmation dialog
        e.preventDefault();
        // Modern browsers ignore custom message, but we can still set it
        e.returnValue = 'Proses parsing sedang berjalan. Jika Anda meninggalkan halaman ini, hasil parsing mungkin akan hilang. Apakah Anda yakin ingin meninggalkan halaman ini?';
        return e.returnValue;
      }
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isProcessing]);

  const updateProcessingStep = (step, status = 'processing') => {
    setProcessingSteps(prev => {
      const updated = [...prev];
      const existingIndex = updated.findIndex(s => s.name === step);
      if (existingIndex >= 0) {
        updated[existingIndex].status = status;
      } else {
        updated.push({ name: step, status });
      }
      return updated;
    });
  };

  const handleFiles = (files) => {
    // Validate file types
    const supportedTypes = ['.pdf', '.jpg', '.jpeg', '.png'];
    const invalidFiles = files.filter(file => 
      !supportedTypes.some(type => file.name.toLowerCase().endsWith(type))
    );
    
    if (invalidFiles.length > 0) {
      setUploadStatus({
        type: 'error',
        message: `File type not supported: ${invalidFiles[0].name}. Please select PDF, JPG, JPEG, or PNG files.`
      });
      return;
    }

    // Validate file sizes
    const currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    const newFilesTotalSize = files.reduce((sum, f) => sum + f.size, 0);
    const totalSize = currentTotalSize + newFilesTotalSize;
    
    const oversizedFiles = files.filter(file => file.size > config.MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setUploadStatus({
        type: 'error',
        message: `File size too large: ${oversizedFiles[0].name}. Maximum size is 50MB per file.`
      });
      return;
    }

    // Add new files to selected files list
    setSelectedFiles(prev => [...prev, ...files]);
    setUploadStatus({
      type: 'success',
      message: `${files.length} file berhasil ditambahkan. Klik tombol "Process" untuk memulai parsing.`
    });
  };

  const handleProcess = async () => {
    if (selectedFiles.length === 0) {
      setUploadStatus({
        type: 'error',
        message: 'Harap pilih file terlebih dahulu.'
      });
      return;
    }

    setIsProcessing(true);
    setProcessingSteps([]);
    
    // Notify parent about processing state
    if (onProcessingChange) {
      onProcessingChange(true);
    }
    
    setUploadStatus({
      type: 'info',
      message: `Starting AI processing for ${selectedFiles.length} file(s)...`
    });

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      // Show processing steps
      updateProcessingStep('File Upload', 'processing');
      updateProcessingStep('Optical Character Recognition', 'pending');
      updateProcessingStep('Analisis dan ekstraksi data', 'pending');
      updateProcessingStep('Bank Statement Analysis', 'pending');
      updateProcessingStep('Fraud Detection', 'pending');

      // Prepare headers with authentication
      // Don't set Content-Type for FormData - browser will set it automatically with boundary
      const headers = addAuthHeader({});

      const response = await fetch(getFullApiUrl('SMART_PARSER'), {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        updateProcessingStep('File Upload', 'completed');
        updateProcessingStep('Optical Character Recognition', 'completed');
        updateProcessingStep('Analisis dan ekstraksi data', 'completed');
        updateProcessingStep('Bank Statement Analysis', 'completed');
        updateProcessingStep('Fraud Detection', 'completed');

        setUploadStatus({
          type: 'success',
          message: `Successfully processed ${result.files_processed} file(s) with ${result.total_transactions} transactions!`
        });

        // Clear selected files after successful processing
        setSelectedFiles([]);

        if (onProcessingComplete) {
          onProcessingComplete(result.transactions, result.fraud_analysis);
        }
      } else {
        throw new Error(result.detail || result.message || 'Failed to process files with AI');
      }
    } catch (error) {
      console.error('Smart processing error:', error);
      updateProcessingStep('File Upload', 'error');
      setUploadStatus({
        type: 'error',
        message: `Error: ${error.message}`
      });
    } finally {
      setIsProcessing(false);
      
      // Notify parent about processing state
      if (onProcessingChange) {
        onProcessingChange(false);
      }
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed': return 'fa-check-circle';
      case 'processing': return 'fa-spinner fa-spin';
      case 'error': return 'fa-exclamation-triangle';
      default: return 'fa-clock';
    }
  };
  
  const getStepIconColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'processing': return 'text-blue-500';
      case 'error': return 'text-red-500';
      default: return 'text-gray-400';
    }
  };

  return (
    <section className="mb-8">
      <div 
        className={`bg-white dark:bg-gray-800 rounded-lg border border-dashed p-8 text-center transition-colors ${
          isDragOver 
            ? 'border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-900/30' 
            : 'border-gray-300 dark:border-gray-600'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="max-w-md mx-auto">
          <div className="text-primary-600 mb-4">
            <i className="fa-solid fa-robot text-4xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">
            AI-Powered Bank Statement Parser
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-300 mb-6 transition-colors duration-300">
            Upload multiple files (PDF, JPG, JPEG, PNG) and let our AI extract bank statement transactions automatically
          </p>
          <label htmlFor="smart-file-upload" className="cursor-pointer">
            <span className={`font-medium py-2.5 px-5 rounded-lg inline-flex items-center ${
              isProcessing 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}>
              <i className="fa-solid fa-upload mr-2"></i>
              {isProcessing ? 'Processing with AI...' : 'Upload File'}
            </span>
            <input
              id="smart-file-upload"
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              disabled={isProcessing}
            />
          </label>
          
          {/* Status Message */}
          {uploadStatus && (
            <div className={`mt-4 p-3 rounded-lg text-sm ${
              uploadStatus.type === 'success' ? 'bg-green-50 text-green-700' :
              uploadStatus.type === 'error' ? 'bg-red-50 text-red-700' :
              'bg-blue-50 text-blue-700'
            }`}>
              {uploadStatus.message}
            </div>
          )}
          
          <div className="mt-4 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <i className="fa-solid fa-magic-wand-sparkles mr-2 text-primary-600 dark:text-primary-400"></i>
            <span>Diproses dengan teknologi AI terenkripsi</span>
          </div>
        </div>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-4 transition-colors duration-300">
          <h4 className="text-sm font-medium text-gray-800 dark:text-white mb-3 flex items-center transition-colors duration-300">
            <i className="fa-solid fa-file-lines mr-2 text-primary-600 dark:text-primary-400"></i>
            File yang Dipilih ({selectedFiles.length})
          </h4>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-600 transition-colors duration-300">
                <div className="flex items-center flex-1 min-w-0">
                  <i className="fa-solid fa-file-pdf text-red-500 dark:text-red-400 mr-2 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate transition-colors duration-300" title={file.name}>
                    {file.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0 transition-colors duration-300">
                    ({formatFileSize(file.size)})
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveFile(index)}
                  disabled={isProcessing}
                  className="ml-2 text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  title="Hapus file"
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={handleProcess}
            disabled={isProcessing || selectedFiles.length === 0}
            className={`mt-4 w-full font-medium py-2.5 px-5 rounded-lg inline-flex items-center justify-center ${
              isProcessing || selectedFiles.length === 0
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}
          >
            <i className="fa-solid fa-brain mr-2"></i>
            {isProcessing ? 'Processing with AI...' : 'Process'}
          </button>
        </div>
      )}

      {/* Processing Steps */}
      {processingSteps.length > 0 && (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-lg font-medium text-gray-800 dark:text-white flex items-center transition-colors duration-300">
              <i className="fa-solid fa-cogs mr-2 text-primary-600 dark:text-primary-400"></i>
              AI Processing Pipeline
            </h4>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {processingSteps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <i className={`fa-solid ${getStepIcon(step.status)} mr-3 ${getStepIconColor(step.status)}`}></i>
                  <span className={`text-sm transition-colors duration-300 ${
                    step.status === 'completed' ? 'text-green-700 dark:text-green-300' :
                    step.status === 'processing' ? 'text-blue-700 dark:text-blue-300' :
                    step.status === 'error' ? 'text-red-700 dark:text-red-300' :
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {step.name}
                  </span>
                  {step.status === 'processing' && (
                    <span className="ml-2 text-xs text-blue-600 animate-pulse">
                      Processing...
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SmartUploadSection; 